import React from "react";
import { motion } from "framer-motion";
import { Camera, Map, Sparkles, ArrowLeft, BrainCircuit, MessageSquareText } from "lucide-react";
import { Link } from "react-router-dom";

// --- ORTAK BİLEŞENLER (Anasayfa ile uyumlu) ---
const Section = ({ children, className = "" }) => (
  <div className={`relative w-full flex justify-center px-4 sm:px-6 md:px-12 ${className}`}>
    <div className="relative z-10 w-full max-w-7xl">{children}</div>
  </div>
);

// Seçim Kartı Bileşeni
const ChoiceCard = ({ to, icon: Icon, title, description, colorClass, delay }) => (
  <Link to={to} className="group w-full md:w-1/2 max-w-lg">
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: delay }}
      whileHover={{ scale: 1.02, translateY: -5 }}
      className="relative h-full bg-slate-900/80 border border-slate-700 rounded-3xl p-8 sm:p-12 overflow-hidden backdrop-blur-md shadow-2xl flex flex-col items-center text-center group-hover:border-slate-500 transition-colors duration-300"
    >
      {/* Arkaplan Efekti (Hover sırasında parlar) */}
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 bg-gradient-to-br ${colorClass}`}></div>

      {/* İkon */}
      <div className={`relative z-10 p-6 rounded-full bg-slate-800 mb-8 ring-1 ring-white/10 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
        <Icon size={48} className={`text-white drop-shadow-md`} />
      </div>

      {/* Başlık */}
      <h2 className="relative z-10 text-3xl md:text-4xl font-extrabold text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gray-300 transition-all">
        {title}
      </h2>

      {/* Açıklama */}
      <p className="relative z-10 text-gray-400 text-lg leading-relaxed mb-8 group-hover:text-gray-300 transition-colors">
        {description}
      </p>

      {/* Buton Görünümü */}
      <div className="relative z-10 mt-auto">
        <span className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold bg-gradient-to-r ${colorClass} shadow-lg group-hover:shadow-indigo-500/50 transition-shadow duration-300`}>
          Keşfetmeye Başla <Sparkles size={18} />
        </span>
      </div>
    </motion.div>
  </Link>
);

export default function MekannameAI() {
  return (
    <div className="min-h-screen bg-slate-950 font-sans text-white overflow-x-hidden flex flex-col">
      
      {/* Arkaplan Dekoru (Anasayfa ile uyumlu) */}
      <div className="inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 z-0"></div>
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-indigo-900/20 blur-[120px] rounded-full z-0 pointer-events-none"></div>

      {/* İçerik */}
      <Section className="flex-grow flex flex-col justify-center items-center py-20 relative z-10">
        
        {/* Başlık Alanı */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center justify-center gap-2 px-4 py-1.5 rounded-full bg-indigo-900/30 border border-indigo-500/30 text-indigo-300 text-sm font-medium mb-6">
            <BrainCircuit size={16} /> Mekânname Yapay Zeka Merkezi
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold bg-gradient-to-r from-teal-200 via-indigo-400 to-purple-400 bg-clip-text text-transparent drop-shadow-2xl mb-6">
            Nasıl Keşfetmek İstersin?
          </h1>
          <p className="text-xl text-gray-400">
            İster gördüğün bir yapıyı tanı, ister hayalindeki seyahati planla. Yapay zeka asistanın senin için hazır.
          </p>
        </motion.div>

        {/* Seçim Kartları */}
        <div className="flex flex-col md:flex-row gap-8 w-full justify-center items-stretch">
          
          {/* 1. Seçenek: Görsel Zeka (Fotoğraftan Tanıma) */}
          <ChoiceCard
            to="/upload" // Mevcut rotanız
            icon={Camera}
            title="Görsel Zeka"
            description="Önünde durduğun tarihi yapının ne olduğunu bilmiyor musun? Fotoğrafını yükle, yapay zeka saniyeler içinde tarihini, mimarisini ve hikayesini anlatsın."
            colorClass="from-indigo-600 to-purple-600"
            delay={0.2}
          />

          {/* 2. Seçenek: Seyahat Asistanı (Yeni Özellik) */}
          <ChoiceCard
            to="/ai-assistant" // Yeni rotanız
            icon={MessageSquareText}
            title="Canlı Asistan"
            description="Hafta sonu için plan mı lazım? Bütçeni, zamanını ve zevklerini söyle; asistanın sana özel, optimize edilmiş bir seyahat rotası oluştursun."
            colorClass="from-teal-500 to-emerald-600"
            delay={0.4}
          />

        </div>
      </Section>
    </div>
  );
}